if (!customElements.get('trust-marquee')) {
  customElements.define(
    'trust-marquee',
    class TrustMarquee extends HTMLElement {
      // The strip is moved here, one frame at a time, instead of by the CSS
      // animation. Changing a CSS animation's playbackRate (to ease it to a stop
      // on hover) behaves differently between browsers and can make the strip
      // jump, so the CSS animation is only a fallback for when this script
      // doesn't run.
      connectedCallback() {
        this.track = this.querySelector('.trust-marquee__track');
        if (!this.track) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        // The script is only needed to ease to a stop under a mouse. Without
        // hover-pause, or on touch screens, the CSS animation does the same
        // scroll off the main thread, so leave it to that.
        if (!this.hasAttribute('data-pause-on-hover')) return;
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

        this.progress = 0; // share of one copy scrolled so far, 0 to 1
        this.rate = 1;
        this.target = 1;
        this.direction = this.classList.contains('trust-marquee--reverse') ? -1 : 1;
        this.step = this.step.bind(this);
        this.classList.add('trust-marquee--scripted');

        // Ease the speed down to zero (and back up) rather than stopping dead.
        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.target = 0;
        });
        this.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse') this.target = 1;
        });

        // Only animate while the strip is on screen.
        this.observer = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) this.start();
          else this.stop();
        });
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.stop();
        if (this.observer) this.observer.disconnect();
      }

      readDuration() {
        const seconds = parseFloat(getComputedStyle(this).getPropertyValue('--marquee-duration'));
        return (seconds > 0 ? seconds : 36) * 1000;
      }

      start() {
        if (this.frame) return;
        // Read once here, not every frame: getComputedStyle right after moving
        // the track forced a style recalculation of the whole page each frame.
        this.duration = this.readDuration();
        this.lastTime = performance.now();
        this.frame = requestAnimationFrame(this.step);
      }

      stop() {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      step(now) {
        const elapsed = Math.min(now - this.lastTime, 64);
        this.lastTime = now;

        this.rate += (this.target - this.rate) * (1 - Math.exp(-elapsed / 220));
        if (Math.abs(this.target - this.rate) < 0.001) this.rate = this.target;

        this.progress = (this.progress + (elapsed / this.duration) * this.rate) % 1;

        // The track holds four identical copies, so one copy is 25% of it.
        // Using a percentage keeps the position right if the width changes,
        // for example when the icon font finishes loading.
        const offset = this.direction === 1 ? this.progress : 1 - this.progress;
        this.track.style.transform = `translate3d(${(-25 * offset).toFixed(4)}%, 0, 0)`;

        this.frame = requestAnimationFrame(this.step);
      }
    }
  );
}
